import { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Search, ArrowRight } from 'lucide-react';

export default function FixNamesTab() {
  const [fixNameForm, setFixNameForm] = useState({ oldName: '', newName: '' });
  const [fixingName, setFixingName] = useState(false);
  const [fixNameResult, setFixNameResult] = useState(null);

  const handleFixPlayerName = async (e) => {
    e.preventDefault();
    
    if (!fixNameForm.oldName.trim() || !fixNameForm.newName.trim()) {
      toast.error('Please enter both old and new names');
      return;
    }
    
    if (fixNameForm.oldName.trim() === fixNameForm.newName.trim()) {
      toast.error('Old and new names must be different');
      return;
    }

    setFixingName(true);
    setFixNameResult(null);
    
    try {
      const response = await axios.post(`${API}/admin/fix-player-name`, {
        old_name: fixNameForm.oldName.trim(),
        new_name: fixNameForm.newName.trim()
      });
      
      setFixNameResult(response.data);
      
      if (response.data.scorecards_updated > 0) {
        toast.success(`Fixed ${response.data.player_entries_fixed} entries across ${response.data.scorecards_updated} scorecards!`);
      } else {
        toast.info('No matching entries found with that name');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix player name');
      console.error(error);
    } finally {
      setFixingName(false);
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-sm">
      <div className="p-6 border-b border-stone-200">
        <h2 className="font-barlow font-bold text-xl text-slate-900 uppercase tracking-tight">
          Fix Player Name Typos
        </h2>
        <p className="font-inter text-stone-600 text-sm mt-1">
          Find and replace player names across all historical scorecards. Use this to fix spelling mistakes or typos.
        </p>
      </div>

      <div className="p-6">
        <form onSubmit={handleFixPlayerName} className="max-w-xl space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-6">
            <p className="font-inter text-sm text-amber-800">
              <strong>Important:</strong> Names are stored in &quot;Last, First&quot; format (e.g., &quot;Moreira, Rui&quot;). 
              Make sure to enter names in this exact format.
            </p>
          </div>

          <div>
            <Label htmlFor="oldName" className="font-inter font-medium text-slate-900">
              Current Name (with typo)
            </Label>
            <Input
              id="oldName"
              data-testid="fix-name-old"
              type="text"
              value={fixNameForm.oldName}
              onChange={(e) => setFixNameForm({...fixNameForm, oldName: e.target.value})}
              className="mt-1 bg-white border-stone-300 rounded-sm h-12 font-inter"
              placeholder="e.g., Morreira, Rui"
              required
            />
          </div>

          <div className="flex items-center justify-center">
            <ArrowRight className="w-6 h-6 text-stone-400" strokeWidth={1.5} />
          </div>

          <div>
            <Label htmlFor="newName" className="font-inter font-medium text-slate-900">
              Correct Name
            </Label>
            <Input
              id="newName"
              data-testid="fix-name-new"
              type="text"
              value={fixNameForm.newName}
              onChange={(e) => setFixNameForm({...fixNameForm, newName: e.target.value})}
              className="mt-1 bg-white border-stone-300 rounded-sm h-12 font-inter"
              placeholder="e.g., Moreira, Rui"
              required
            />
          </div>

          <Button
            type="submit"
            data-testid="fix-name-submit"
            disabled={fixingName}
            className="w-full bg-amber-600 text-white hover:bg-amber-700 rounded-sm uppercase tracking-wider font-bold h-12 font-barlow"
          >
            {fixingName ? (
              <>Processing...</>
            ) : (
              <>
                <Search className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Find &amp; Replace
              </>
            )}
          </Button>
        </form>

        {/* Results */}
        {fixNameResult && (
          <div className="mt-6 max-w-xl">
            <div className={`p-4 rounded-sm border ${
              fixNameResult.scorecards_updated > 0 
                ? 'bg-emerald-50 border-emerald-200' 
                : 'bg-stone-50 border-stone-200'
            }`}>
              <h3 className="font-barlow font-bold text-lg text-slate-900 mb-2">
                {fixNameResult.scorecards_updated > 0 ? 'Changes Applied' : 'No Changes Made'}
              </h3>
              <ul className="font-inter text-sm space-y-1">
                <li>
                  <span className="text-stone-600">Scorecards updated:</span>{' '}
                  <span className="font-mono font-bold">{fixNameResult.scorecards_updated}</span>
                </li>
                <li>
                  <span className="text-stone-600">Player entries fixed:</span>{' '}
                  <span className="font-mono font-bold">{fixNameResult.player_entries_fixed}</span>
                </li>
              </ul>
              
              {fixNameResult.affected_dates && fixNameResult.affected_dates.length > 0 && (
                <div className="mt-3 pt-3 border-t border-stone-200">
                  <p className="font-inter text-sm text-stone-600 mb-2">Affected competition dates:</p>
                  <div className="flex flex-wrap gap-2">
                    {fixNameResult.affected_dates.map((date) => (
                      <span key={date} className="px-2 py-1 bg-white border border-stone-200 rounded text-xs font-mono">
                        {date}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
